import { authorizedApi } from './index';
import {
  setValidationResult,
  setSessionId,
  setError,
} from '@/redux/slices/sessionValidation';
import { getVerisoulSession } from '@/common/verisoul';

export const currentUserApi = authorizedApi.injectEndpoints({
  endpoints: (builder) => ({
    getCurrentUser: builder.query({
      async queryFn(_, __, ___, baseQuery) {
        await baseQuery({
          url: '/auth/login',
          method: 'POST',
          credentials: 'include',
        });

        const user = await baseQuery({
          url: '/users/me',
          method: 'get',
        });

        return {
          data: {
            ...user.data,
          },
        };
      },
      async onQueryStarted(_, { dispatch }) {
        dispatch(
          currentUserApi.util.updateQueryData(
            'getCurrentUserProfile',
            undefined,
            (value) => {
              return value;
            },
          ),
        );
      },
    }),
    getCurrentUserRewards: builder.query({
      query: () => ({
        url: '/rewards/me',
        method: 'GET',
      }),
    }),
    getCurrentUserProfile: builder.query({
      async queryFn(arg, api, extraOptions, baseQuery) {
        // First, get user data
        const userResult = await baseQuery({
          url: '/users/me',
          method: 'GET',
        });

        if (userResult.error) {
          return { error: userResult.error };
        }

        // Then validate session and get risk info
        let sessionValidationResult = null;
        try {
          // Wrap in timeout to prevent hanging if Verisoul is blocked by browser (for instance, Brave browser)
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(
              () => reject(new Error('Verisoul session timeout')),
              5000,
            ),
          );

          const session = await Promise.race([
            getVerisoulSession(),
            timeoutPromise,
          ]).catch(() => null);

          const sessionId = session?.session_id;

          if (sessionId) {
            // Store session ID in Redux
            api.dispatch(setSessionId(sessionId));

            const validationResult = await baseQuery({
              url: '/session/validate',
              method: 'POST',
              body: { session_id: sessionId },
            });

            if (!validationResult.error) {
              sessionValidationResult = validationResult.data;
              // Store validation result in Redux
              api.dispatch(
                setValidationResult({
                  result: sessionValidationResult,
                  type: 'authenticated',
                }),
              );
            } else {
              api.dispatch(setError('Failed to validate session'));
            }
          }
        } catch (error) {
          // Continue even if session validation fails
          console.warn('Session validation not available:', error);
          api.dispatch(setError(error.message || 'Session validation failed'));
        }

        // Merge user data with session validation/risk info
        return {
          data: {
            ...userResult.data,
            ...(sessionValidationResult && {
              sessionValidation: sessionValidationResult,
            }),
          },
        };
      },
    }),
    getCurrentUserReferrals: builder.query({
      query: () => '/rewards/me/referrals',
    }),
    setReferral: builder.mutation({
      query: (referralCode) => ({
        url: '/users/referral',
        method: 'POST',
        body: { referralCode },
      }),
    }),
    completeOnboarding: builder.mutation({
      query: () => ({
        url: '/users/me/onboarding-completed',
        method: 'POST',
      }),
    }),
    setFirebaseToken: builder.mutation({
      query: (token) => ({
        url: '/firebase/token',
        method: 'POST',
        body: { token },
      }),
    }),
    testPushNotification: builder.mutation({
      query: (email) => ({
        url: '/admin/firebase/test',
        method: 'POST',
        body: { email },
      }),
    }),
    deleteAccount: builder.mutation({
      query: (reason) => ({
        url: '/users/delete-account',
        method: 'POST',
        body: { reason },
      }),
    }),
    setUtmData: builder.mutation({
      query: ({ utm_source, utm_medium, utm_campaign }) => ({
        url: '/users/me/utm-data',
        method: 'POST',
        body: { utm_source, utm_medium, utm_campaign },
      }),
    }),
  }),
});

export const {
  useGetCurrentUserQuery,
  useGetCurrentUserRewardsQuery,
  useGetCurrentUserProfileQuery,
  useSetReferralMutation,
  useSetFirebaseTokenMutation,
  useTestPushNotificationMutation,
  useLazyGetCurrentUserProfileQuery,
  useDeleteAccountMutation,
  useGetCurrentUserReferralsQuery,
  useCompleteOnboardingMutation,
  useSetUtmDataMutation,
} = currentUserApi;
